import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { productCatalog, enrichDetails } from './instrumentDetails';
import './InstrumentDetailsPage.css'

// Build products from productCatalog with stable ids matching ProductsPage
const allProducts = Object.entries(productCatalog).flatMap(([catKey, items]) =>
  items.map((item, idx) => ({
    id: `${catKey}-${idx + 1}`,
    name: item.name,
    category: catKey,
    image: item.image,
    description: `${item.name} — premium quality instrument.`,
  }))
);

export default function InstrumentDetailsPage() {
  const { id } = useParams();
  const instrument = React.useMemo(() => allProducts.find((p) => String(p.id) === String(id)), [id]);

  if (!instrument) {
    return (
      <section className="instrument-details" style={{ paddingTop: 100 }}>
        <div className="container" style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2>Instrument Not Found</h2>
          <p>We couldn't find details for this instrument.</p>
          <Link to="/products" className="btn btn-primary">Back to Products</Link>
        </div>
      </section>
    );
  }

  const details = enrichDetails({ category: instrument.category });

  const handleImageError = (e) => {
    e.target.alt = 'Image not available';
    e.target.style.opacity = '0.2';
    e.target.style.background = '#eee';
    e.target.src = 'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="480" height="360"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="Arial" font-size="16">Image not available</text></svg>';
  };

  return (
    <section className="instrument-details section">
      <div className="container" style={{ maxWidth: 1100 }}>
        <Link to="/products" className="breadcrumb-link">&larr; Back to Products</Link>
        <div className="details-grid">
          <div className="details-card">
            <div className="details-image">
              <img
                src={instrument.image}
                alt={instrument.name}
                onError={handleImageError}
              />
            </div>
          </div>
          <div className="details-card details-content">
            <div className="details-header">
              <h2>{instrument.name}</h2>
              <span className="details-badge">{instrument.category.replace('_',' ')}</span>
            </div>
            <p className="details-description">{instrument.description}</p>

            <div className="spec-section">
              <h3>Specifications</h3>
              <div className="spec-grid">
                <div className="spec-item">
                  <span className="spec-label">Materials</span>
                  <span className="spec-value">{details.materials}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Sterilization</span>
                  <span className="spec-value">{details.sterilization}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Specs</span>
                  <span className="spec-value">{details.specs}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Usage</span>
                  <span className="spec-value">{details.usage}</span>
                </div>
              </div>
            </div>

            {details.source && (
              <div className="spec-section">
                <h3>Source</h3>
                <p className="details-note">{details.source}</p>
              </div>
            )}

            <div className="details-actions">
              <Link to="/#contact" className="btn btn-primary">Request a Quote</Link>
              <Link to="/products" className="btn btn-outline">Back to Products</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}